const PC_BREAKPOINT = 1280;
const EXTRA_SETS    = 2;
const FRICTION      = 0.95; // 慣性減衰（60fps基準）

function loadVideo(li: HTMLElement, muted: boolean): void {
  const id = li.dataset.ytId;
  if (!id || li.querySelector('iframe')) return;
  const figure = li.querySelector('figure');
  if (!figure) return;
  const params = new URLSearchParams({
    autoplay: '1',
    mute: muted ? '1' : '0',
    controls: '0',
    modestbranding: '1',
    iv_load_policy: '3',
    rel: '0',
    playsinline: '1',
  });
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.youtube.com/embed/${id}?${params}`;
  iframe.allow = 'autoplay; encrypted-media';
  iframe.setAttribute('allowfullscreen', '');
  figure.appendChild(iframe);

  // iframe は別ブラウジングコンテキストのためクリックを吸収する。
  // 透明オーバーレイを重ねてクリックを <a> に伝達する。
  const overlay = document.createElement('div');
  overlay.className = 'js-video-overlay';
  overlay.style.cssText = 'position:absolute;inset:0;z-index:1;cursor:pointer;';
  (figure as HTMLElement).style.position = 'relative';
  figure.appendChild(overlay);
}

function unloadVideo(li: HTMLElement): void {
  li.querySelector('iframe')?.remove();
  li.querySelector('.js-video-overlay')?.remove();
}

export function initVideoSlider(): void {
  const ul      = document.querySelector<HTMLElement>('.video ul');
  const btnPrev = document.querySelector<HTMLElement>('.js-video-prev');
  const btnNext = document.querySelector<HTMLElement>('.js-video-next');
  if (!ul) return;

  const originals = Array.from(ul.children) as HTMLElement[];
  if (!originals.length) return;

  // ---- 無限ループ用クローン生成 ----
  for (let i = 0; i < EXTRA_SETS; i++) {
    const anchor = ul.firstChild;
    originals.forEach(el => ul.insertBefore(el.cloneNode(true) as HTMLElement, anchor));
    originals.forEach(el => ul.appendChild(el.cloneNode(true) as HTMLElement));
  }

  const allItems = Array.from(ul.children) as HTMLElement[];

  // ---- PC：ホバーで動画読み込み ----
  allItems.forEach(li => {
    li.addEventListener('mouseenter', () => {
      if (window.innerWidth < PC_BREAKPOINT) return;
      loadVideo(li, true);
    });
    li.addEventListener('mouseleave', () => {
      if (window.innerWidth < PC_BREAKPOINT) return;
      unloadVideo(li);
    });
  });

  // ---- レイアウト確定後に初期化 ----
  requestAnimationFrame(() => {
    const N           = originals.length;
    const originFirst = allItems[EXTRA_SETS * N];
    if (!originFirst) return;

    const scrollPad   = parseFloat(getComputedStyle(ul).scrollPaddingLeft) || 0;
    const oneSetWidth = allItems[N].offsetLeft - allItems[0].offsetLeft;

    ul.scrollLeft = originFirst.offsetLeft - scrollPad;

    // ---- 無限ループ：端に近づいたらジャンプ ----
    // ユーザーが居られる範囲: set 1 〜 set (2*EXTRA_SETS-1)
    const minScroll = allItems[N].offsetLeft - scrollPad;
    // maxScroll は実際にスクロールできる上限より手前の set (2*EXTRA_SETS-1) 先頭
    const rawMax    = allItems[(2 * EXTRA_SETS - 1) * N].offsetLeft - scrollPad;
    const maxScroll = Math.min(rawMax, ul.scrollWidth - ul.clientWidth - 1);
    const jumpAmt   = 2 * oneSetWidth;

    let isJumping = false;

    const doJumpCheck = () => {
      if (isJumping) return;
      const sl = ul.scrollLeft;
      if (sl < minScroll) {
        isJumping = true;
        ul.scrollLeft = sl + jumpAmt;
        // scrollend まで isJumping を保持して二重ジャンプを防ぐ
        ul.addEventListener('scrollend', () => { isJumping = false; }, { once: true });
        setTimeout(() => { isJumping = false; }, 400);
      } else if (sl >= maxScroll) {
        isJumping = true;
        ul.scrollLeft = sl - jumpAmt;
        ul.addEventListener('scrollend', () => { isJumping = false; }, { once: true });
        setTimeout(() => { isJumping = false; }, 400);
      }
    };

    // ---- SP：左端スナップアイテムを自動再生 ----
    let activeLi: HTMLElement | null = null;

    const setActive = (li: HTMLElement) => {
      if (li === activeLi) return;
      if (activeLi) unloadVideo(activeLi);
      activeLi = li;
      loadVideo(li, true);
    };

    const updateLeftmost = () => {
      if (window.innerWidth >= PC_BREAKPOINT) return;
      const targetX = ul.scrollLeft + scrollPad;
      let closest   = allItems[0];
      let minDist   = Infinity;
      for (const item of allItems) {
        const dist = Math.abs(item.offsetLeft - targetX);
        if (dist < minDist) { minDist = dist; closest = item; }
      }
      setActive(closest);
    };

    // ネイティブのリンク・画像ドラッグを無効化（カスタムドラッグと競合するため）
    ul.addEventListener('dragstart', (e) => e.preventDefault(), true);

    // ---- トラックパッド慣性スクロール中はスナップを無効化 ----
    // wheel イベントは慣性フェーズ中も発火し続け、止まると止まる。
    // deltaX があるときだけ scroll-snap を切り、150ms 途絶えたら復元する。
    let wheelSnapTimer: ReturnType<typeof setTimeout>;
    ul.addEventListener('wheel', (e: WheelEvent) => {
      if (e.deltaX === 0) return;
      ul.style.scrollSnapType = 'none';
      clearTimeout(wheelSnapTimer);
      wheelSnapTimer = setTimeout(() => {
        ul.style.scrollSnapType = '';
      }, 150);
    }, { passive: true });

    // scrollend（モダンブラウザ）＋ debounce フォールバック
    let scrollTimer: ReturnType<typeof setTimeout>;
    ul.addEventListener('scrollend', () => {
      if (!isJumping) updateLeftmost();
    });
    ul.addEventListener('scroll', () => {
      doJumpCheck();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { if (!isJumping) updateLeftmost(); }, 120);
    }, { passive: true });

    requestAnimationFrame(updateLeftmost);

    // ---- スナップ先へスムーズスクロール ----
    const snapToNearest = (fromLeft: number) => {
      let nearestSnapLeft = allItems[0].offsetLeft - scrollPad;
      let minDist = Infinity;
      for (const item of allItems) {
        const snapLeft = item.offsetLeft - scrollPad;
        const dist     = Math.abs(snapLeft - fromLeft);
        if (dist < minDist) { minDist = dist; nearestSnapLeft = snapLeft; }
      }
      isJumping = true;
      ul.scrollTo({ left: nearestSnapLeft, behavior: 'smooth' });
      // scrollend か 1.5s 後にリセット
      let done = false;
      const reset = () => { if (!done) { done = true; isJumping = false; updateLeftmost(); } };
      ul.addEventListener('scrollend', reset, { once: true });
      setTimeout(reset, 1500);
    };

    // ---- 左右ボタン：1アイテム分スクロール ----
    const scrollByOne = (dir: 'next' | 'prev') => {
      ul.scrollTo({ left: ul.scrollLeft, behavior: 'instant' });
      clearTimeout(wheelSnapTimer);
      isJumping = false;
      inertiaId++;

      const currentSL = ul.scrollLeft;
      let nearestIdx  = 0;
      let minDist     = Infinity;
      allItems.forEach((item, idx) => {
        const dist = Math.abs(item.offsetLeft - scrollPad - currentSL);
        if (dist < minDist) { minDist = dist; nearestIdx = idx; }
      });

      const targetIdx    = dir === 'next' ? nearestIdx + 1 : nearestIdx - 1;
      const clampedIdx   = Math.max(0, Math.min(allItems.length - 1, targetIdx));
      const targetSL     = allItems[clampedIdx].offsetLeft - scrollPad;

      isJumping = true;
      ul.scrollTo({ left: targetSL, behavior: 'smooth' });
      let done = false;
      const reset = () => { if (!done) { done = true; isJumping = false; doJumpCheck(); updateLeftmost(); } };
      ul.addEventListener('scrollend', reset, { once: true });
      setTimeout(reset, 1500);
    };

    btnPrev?.addEventListener('click', () => scrollByOne('prev'));
    btnNext?.addEventListener('click', () => scrollByOne('next'));

    // ---- マウスドラッグ + 慣性 ----
    // setPointerCapture を使わず document レベルで拾うことで
    // click イベントを <a> まで自然伝播させる。
    let dragging  = false;
    let startX    = 0;
    let startSL   = 0;
    let lastX     = 0;
    let lastT     = 0;
    let velX      = 0;
    let didDrag   = false;
    let inertiaId = 0;

    const onDocMove = (e: PointerEvent) => {
      if (!dragging || e.pointerType !== 'mouse') return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) didDrag = true;
      const dt = e.timeStamp - lastT;
      if (dt > 0) velX = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = e.timeStamp;
      ul.scrollLeft = startSL - dx;
      if (didDrag) e.preventDefault();
    };

    const onDocUp = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      document.removeEventListener('pointermove',   onDocMove);
      document.removeEventListener('pointerup',     onDocUp);
      document.removeEventListener('pointercancel', onDocUp);
      if (!dragging) return;
      dragging                = false;
      ul.style.cursor         = '';
      ul.style.scrollSnapType = '';
      ul.style.userSelect     = '';
      if (!didDrag) return;

      // 慣性アニメ開始
      const myId = ++inertiaId;
      let vel    = -velX;
      let prevT  = performance.now();
      const step = (now: number) => {
        if (inertiaId !== myId) return;
        const dt = now - prevT;
        prevT    = now;
        vel     *= Math.pow(FRICTION, dt / 16.67);
        ul.scrollLeft += vel * dt;
        doJumpCheck();
        if (Math.abs(vel) > 0.05) {
          requestAnimationFrame(step);
        } else {
          snapToNearest(ul.scrollLeft);
        }
      };
      requestAnimationFrame(step);
    };

    ul.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      // ウィンドウ外でボタンを離した場合などに残留したリスナーをクリア
      document.removeEventListener('pointermove',   onDocMove);
      document.removeEventListener('pointerup',     onDocUp);
      document.removeEventListener('pointercancel', onDocUp);
      // 進行中のスムーズスクロール（snapToNearest）を即座にキャンセル
      ul.scrollTo({ left: ul.scrollLeft, behavior: 'instant' });
      clearTimeout(wheelSnapTimer);
      isJumping = false;
      inertiaId++;
      dragging  = true;
      didDrag   = false;
      startX    = lastX = e.clientX;
      startSL   = ul.scrollLeft;
      lastT     = e.timeStamp;
      velX      = 0;
      ul.style.cursor         = 'grabbing';
      ul.style.scrollSnapType = 'none';
      ul.style.userSelect     = 'none';
      document.addEventListener('pointermove',   onDocMove, { passive: false });
      document.addEventListener('pointerup',     onDocUp);
      document.addEventListener('pointercancel', onDocUp);
    });

    ul.addEventListener('click', (e: MouseEvent) => {
      if (didDrag) { e.preventDefault(); e.stopPropagation(); didDrag = false; }
    }, true);
  });
}
